import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule, mapToCanActivate } from '@angular/router';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { FieldsetModule } from 'primeng/fieldset';
import { InputSwitchModule } from 'primeng/inputswitch';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TreeTableModule } from 'primeng/treetable';
import { TabViewModule } from 'primeng/tabview';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { DividerModule } from 'primeng/divider';
import { AppComponent } from './app.component';
import { CarrierComponent } from './carrier/carrier.component';
import { CarrierService } from './carrier/carrier.service';
import { Globals } from './globals';
import { MessagesComponent } from './messages/messages.component';
import { MessagesService } from './messages/messages.service';
import { HamiltonLRSFileNameFormatPipe } from './pipes/hamilton-lrs-file-name-format.pipe';
import { TubeSearchFilterPipe } from './pipes/tube-searchFilterPipe';
import { DataViewModule } from 'primeng/dataview';
import { MasterdataComponent } from './masterdata/masterdata.component';
import { LoginComponent } from './login/login.component';
import { AuthGuard } from './common-services/auth.guard';
import { AuthenticationService } from './common-services/authentication.service';
import { AzureAppInsightsService } from './common-services/appinsights.azure.service';
import { MasterDataService } from './masterdata/masterdata.service';
import { CheckboxModule } from 'primeng/checkbox';
import { SkeletonModule } from 'primeng/skeleton';
import { OrderListModule } from 'primeng/orderlist';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressTrackerComponent } from './progress-tracker/progress-tracker.component';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { LabComponent } from './lab/lab.component';
import { HeaderComponent } from './header/header.component';
import { LabGuard } from './common-services/lab.guard';
import { LabEmitterService } from './common-services/lab-emitter.service';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SpinnerModule } from 'primeng/spinner';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { ClientNamePipe } from './pipes/client-name.pipe';
import { ListboxModule } from 'primeng/listbox';
import { PanelModule } from 'primeng/panel';
import { PaginatorModule } from 'primeng/paginator';
import { SidebarModule } from "primeng/sidebar";
import { BarcodeTemplateComponent } from './barcode-template/barcode-template.component';
import { MdmProcessStepService } from './mdm-process-step/mdm-process-step.service';
import { PickListModule } from 'primeng/picklist';

import { DatePipe } from '@angular/common';
import { AppService } from './app.service';
import { MaSendersTemplateComponent } from './masenders-template/masenders-template.component';

import { AzureAuthGuard } from './common-services/auth.azure.guard';
import { AccessTokenGuard } from './common-services/access.token.guard';
import { AccessTokenInterceptor } from './common-services/access.token.interceptor';
import { KitsTemplateComponent } from './kits-template/kits-template.component';
import { MdmGuard } from './common-services/mdm.guard';
import { JwtInterceptor } from './common-services/jwt.interceptor';
import { ApgDeliveryComponent } from './apg-delivery/apg-delivery.component';
import { ApgDeliveryService } from './apg-delivery/apg-delivery.service';
import { BUGuard } from './common-services/bu.guard';
import { environment } from "../environments/environment";
import { RefreshAuthenticationService } from './common-services/access-token-refresh';
import { ApgMachineStatusComponent } from './apg-machine-status/apg-machine-status.component';
import { APGMachineStatusService } from './apg-machine-status/apg-machine-service';
import { ApgAdministrationComponent } from './apg-administration/apg-administration.component';
import { APGAdministrationService } from './apg-administration/apg-administration.service';
import { ApgDeliverBoxManagerComponent } from './apg-deliver-box-manager/apg-deliver-box-manager.component';
import { ManufacturerConfigComponent } from './mdm-manufacturer-config/manufacturer-config.component';
import { RedoReasonsComponent } from './redo-reasons/redo-reasons.component';
import { InboundFileProcessingComponent } from './inbound-file-processing/inbound-file-processing.component';
import { WorkFlowComponent } from './work-flow/work-flow.component';
import { MdmControlsComponent } from './mdm-controls/mdm-controls.component';
import { EventEmitterService } from './event-emitter.service';
import { ApgAnnouncedComponent } from './apg-announced/apg-announced.component';
import { MaReceptionComponent } from './ma-reception/ma-reception.component';
import { MaReceptionService } from './ma-reception/ma-reception.service';
import { MaPlatePreparationComponent } from './ma-plate-preparation/ma-plate-preparation.component';
import { MaPlatePreparationService } from './ma-plate-preparation/ma-plate-preparation.service';
import { MaSamplesComponent } from './ma-samples/ma-samples.component';
import { MaResultEvaluationService } from './ma-result-evaluation/ma-result-evaluation.service';
import { PlateStatus1Component } from './plate-status1/plate-status1.component';

import { MaBeadchipLoadingComponent } from './ma-beadchip-loading/ma-beadchip-loading.component';
import { MaBeadchipComponent } from './ma-beadchip/ma-beadchip.component';
import { AdminSearchComponent } from './admin-search/admin-search/admin-search.component';
import { ChemicalComponent } from './chemical/chemical.component';
import { MaCommentsPopupComponent } from './ma-comments-popup/ma-comments-popup.component';
import { SearchComponent } from './search/search.component';
import { LotTrackingComponent } from './lot-tracking/lot-tracking.component';
import { MaChemicalsPopupComponent } from './ma-chemicals-popup/ma-chemicals-popup.component';
import { MaStorageComponent } from './ma-storage/ma-storage.component';
import { StatusScreenComponent } from './status-screen/status-screen.component';
import { SendersTemplateComponent } from './senders-template/senders-template.component';
import { MdmTagSystemComponent } from './mdm-tag-system/mdm-tag-system.component';
import { TagsComponent } from './mdm-tags/tags.component';
import { MdmFlowcellComponent } from './mdm-flowcell/mdm-flowcell.component';
import { MdmAnalysisComponent } from './mdm-analysis/mdm-analysis.component';
import { NGReceptionComponent } from './ng-reception/ng-reception.component';
import { RunPlanningComponent } from './run-planning/run-planning.component';
import { RunSchedulingComponent } from './run-scheduling/run-scheduling.component';
import { RunProcessingComponent } from './run-processing/run-processing.component';
import { RunComponent } from './run/run.component';
import { NgsProcessComponent } from './ngs-process/ngs-process.component';
import { APGTestingService } from './agptesting-newview/apgtesting.service';
import { AppWorkflowNgsComponent } from './app-workflow-ngs/app-workflow-ngs.component';
import { RunPlanningService } from './run-planning/run-planning.service';
import { NumberFormatPipe } from './pipes/number.pipe';
import { PlateViewComponent } from './plate-view/plate-view.component';
import { ApgSampleResultViewComponent } from './apg-sample-result-view/apg-sample-result-view.component';
import { RunSchedulingService } from './run-scheduling/run-scheduling.service';
import { RunProcessingService } from './run-processing/run-processing.service';
import { ICBFWorkflowComponent } from './icbf-workflow/icbf-workflow.component';
import { MaPreparationComponent } from './ma-preparation/ma-preparation.component';
import { MaPreparationExtractionComponent } from './ma-preparation-extraction/ma-preparation-extraction.component';
import { MdmProcessflowComponent } from './mdm-processflow/mdm-processflow.component';
import { MaPreparationQcScanComponent } from './ma-preparation-qc-scan/ma-preparation-qc-scan.component';
import { MaPreparationMergeComponent } from './ma-preparation-merge/ma-preparation-merge.component';
import { MaBeadchipViewComponent } from './ma-beadchip-view/ma-beadchip-view.component';
import { MaMachineComponent } from './ma-machine/ma-machine.component';
import { MaMachineService } from './ma-machine/ma-machine.service';
import { MaPlatePreparationAxiomComponent } from './ma-plate-preparation-axiom/ma-plate-preparation-axiom.component';
import { MaBeadchipLoadingAxiomComponent } from './ma-beadchip-loading-axiom/ma-beadchip-loading-axiom.component';
import { MaResultEvaluationComponent } from './ma-result-evaluation/ma-result-evaluation.component';
import { ToastModule } from 'primeng/toast';
import { MaSamplePlacementComponent } from './ma-sample-placement/ma-sample-placement.component';
import { MessageService, ConfirmationService } from 'primeng/api';
import { CalendarModule } from 'primeng/calendar';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ColorPickerModule } from 'primeng/colorpicker';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { ChipModule } from 'primeng/chip';
import { AccordionModule } from 'primeng/accordion';
import { MessagesModule } from 'primeng/messages';
import { GalleriaModule } from 'primeng/galleria';
import { NgxFileHelpersModule } from 'ngx-file-helpers';
import { TreeModule } from 'primeng/tree';
import { SplitButtonModule } from 'primeng/splitbutton';
import { ConfigurationService } from './protocol-configuration/configuration.service';
import { MessagesNewComponent } from './messages-new/messages.component';
import { MessagesNewService } from './messages-new/messages.service';
import { NavbarComponent } from './navbar/navbar.component';
import { HistoryComponent } from './history/history.component';
import { PlateSearchComponent } from './plate-search/plate-search.component';
import { SampleSearchComponent } from './sample-search/sample-search.component';
import { NavOpenEmitterService } from './common-services/nav-open-emitter.service';
import { SharedComponentsModule } from './shared-components.module';
import { unsavedChangesGuard } from './unsaved-changes.guard';
import { PlateProcessingService } from './plate-processing/plate-processing.service';
import { StorageNewComponent } from './storage-new/storage-new.component';
import { MachinesNewComponent } from './machines-new/machines-new.component';
import { MachineNewService } from './machines-new/machines-new.service';

const ALL_USER_ACCESS = [
    environment.AZURE_USER_DK_DTC_POPGEN_GROUP_CLAIM,
    environment.AZURE_USER_DE_FOOD_ENVIRONMENT_GROUP_CLAIM,
    environment.AZURE_USER_US_EBDI_GROUP_CLAIM,
    environment.AZURE_USER_GROUP_CLAIM,
    environment.AZURE_CONFIG_USER_CLAIM,
    environment.AZURE_USER_DK_GROUP_CLAIM
]

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    LabComponent,
    CarrierComponent,
    MessagesComponent,
    HamiltonLRSFileNameFormatPipe,
    TubeSearchFilterPipe,
    MasterdataComponent,
    LoginComponent,
    ProgressTrackerComponent,
    ClientNamePipe,
    BarcodeTemplateComponent,
    MaSendersTemplateComponent,
    SendersTemplateComponent,
    KitsTemplateComponent,
    ApgDeliveryComponent,
    ApgMachineStatusComponent,
    ApgAdministrationComponent,
    ApgDeliverBoxManagerComponent,
    ManufacturerConfigComponent,
    RedoReasonsComponent,
    InboundFileProcessingComponent,
    WorkFlowComponent,
    MdmControlsComponent,
    ApgAnnouncedComponent,
    MaReceptionComponent,
    MaPlatePreparationComponent,
    MaSamplesComponent,
    PlateStatus1Component,
    MaBeadchipLoadingComponent,
    MaBeadchipComponent,
    AdminSearchComponent,
    ChemicalComponent,
    MaCommentsPopupComponent,
    SearchComponent,
    LotTrackingComponent,
    MaChemicalsPopupComponent,
    MaStorageComponent,
    StatusScreenComponent,
    MdmTagSystemComponent,
    TagsComponent,
    MdmFlowcellComponent,
    MdmAnalysisComponent,
    NGReceptionComponent,
    RunPlanningComponent,
    RunSchedulingComponent,
    RunProcessingComponent,
    RunComponent,
    NgsProcessComponent,
    AppWorkflowNgsComponent,
    NumberFormatPipe,
    PlateViewComponent,
    ApgSampleResultViewComponent,
    ICBFWorkflowComponent,
    MaPreparationComponent,
    MaPreparationExtractionComponent,
    MdmProcessflowComponent,
    MaPreparationQcScanComponent,
    MaPreparationMergeComponent,
    MaBeadchipViewComponent,
    MaMachineComponent,
    MaPlatePreparationAxiomComponent,
    MaBeadchipLoadingAxiomComponent,
    MaResultEvaluationComponent,
    MaSamplePlacementComponent,

    //New UI Components
    NavbarComponent,
    MessagesNewComponent,
    HistoryComponent,
    PlateSearchComponent,
    SampleSearchComponent,
    StorageNewComponent,
    MachinesNewComponent
  ],
  imports: [
    BrowserModule,
    TableModule,
    TreeModule,
    TreeTableModule,
    TabViewModule,
    BrowserAnimationsModule,
    HttpClientModule,
    TieredMenuModule,
    SkeletonModule,
    DividerModule,
    InputSwitchModule,
    DialogModule,
    FormsModule,
    DropdownModule,
    FieldsetModule,
    InputTextModule,
    ListboxModule,
    AutoCompleteModule,
    CalendarModule,
    DataViewModule,
    MessagesModule,
    CheckboxModule,
    OverlayPanelModule,
    OrderListModule,
    TooltipModule,
    NgxChartsModule,
    SplitButtonModule,
    NgxFileHelpersModule,
    RadioButtonModule,
    SpinnerModule,
    ScrollPanelModule,
    PanelModule,
    ColorPickerModule,
    ConfirmDialogModule,
    SelectButtonModule,
    ToggleButtonModule,
    GalleriaModule,
    PickListModule,
    PaginatorModule,
    SidebarModule,
    ChipModule,
    AccordionModule,
    ToastModule,
    SharedComponentsModule,
    RouterModule.forRoot([
      { path: '', component: HeaderComponent, canActivate: mapToCanActivate([AzureAuthGuard, LabGuard])},
      { path: 'lab', component: LabComponent, canActivate: mapToCanActivate([AccessTokenGuard])},
      { path: 'login', component: LoginComponent, canActivate: mapToCanActivate([AzureAuthGuard, LabGuard, BUGuard])},
      { path: 'admin/masterdata', component: MasterdataComponent, canActivate: mapToCanActivate([AzureAuthGuard, LabGuard, MdmGuard, AuthGuard])},
      { path: 'logout', component: LoginComponent, canActivate: mapToCanActivate([AzureAuthGuard, LabGuard, BUGuard])},
      { path: 'apg-delivery', component: ApgDeliveryComponent, canActivate: mapToCanActivate([AzureAuthGuard, LabGuard, BUGuard]), data: { claim: [environment.AZURE_USER_GROUP_CLAIM, environment.AZURE_USER_DK_GROUP_CLAIM] } },
      { path: 'apg-delivery-box-manager', component: ApgDeliverBoxManagerComponent, canActivate: mapToCanActivate([AzureAuthGuard, LabGuard, BUGuard]), data: { claim: [environment.AZURE_USER_GROUP_CLAIM, environment.AZURE_USER_DK_GROUP_CLAIM] } },
      { path: 'reception', component: MaReceptionComponent, canActivate: mapToCanActivate([AzureAuthGuard, LabGuard, BUGuard]), data: { claim: [environment.AZURE_USER_GROUP_CLAIM, environment.AZURE_USER_DK_GROUP_CLAIM] } },
      { path: 'reception/:id/:tab', component: MaReceptionComponent, canActivate: mapToCanActivate([AzureAuthGuard, LabGuard, BUGuard]), data: { claim: [environment.AZURE_USER_GROUP_CLAIM, environment.AZURE_USER_DK_GROUP_CLAIM] } },
      { path: 'plate-preparation', component: MaPlatePreparationComponent, canActivate: mapToCanActivate([AzureAuthGuard, LabGuard, BUGuard]), data: { claim: [environment.AZURE_USER_GROUP_CLAIM, environment.AZURE_USER_DK_GROUP_CLAIM] } },
      { path: 'samples', component: MaSamplesComponent, canActivate: mapToCanActivate([AzureAuthGuard, LabGuard, BUGuard]), data: { claim: [environment.AZURE_USER_GROUP_CLAIM, environment.AZURE_USER_DK_GROUP_CLAIM] } },
      { path: 'samples/:id/:tab/:entity', component: MaSamplesComponent, canActivate: mapToCanActivate([AzureAuthGuard, LabGuard, BUGuard]), data: { claim: [environment.AZURE_USER_GROUP_CLAIM, environment.AZURE_USER_DK_GROUP_CLAIM] } },
      { path: 'preparation', component: MaPreparationComponent, canActivate: mapToCanActivate([AzureAuthGuard, LabGuard, BUGuard]), data: { claim: [environment.AZURE_USER_GROUP_CLAIM, environment.AZURE_USER_DK_GROUP_CLAIM] } },
      { path: 'preparation/:id/:tab', component: MaPreparationComponent, canActivate: mapToCanActivate([AzureAuthGuard, LabGuard, BUGuard]), data: { claim: [environment.AZURE_USER_GROUP_CLAIM, environment.AZURE_USER_DK_GROUP_CLAIM] } },
      { path: 'plate-status',component:PlateStatus1Component,canActivate: mapToCanActivate([AzureAuthGuard, LabGuard, BUGuard]), data: { claim: [environment.AZURE_USER_GROUP_CLAIM, environment.AZURE_USER_DK_GROUP_CLAIM]} },
      { path: 'Admin-Search',component:AdminSearchComponent,canActivate: mapToCanActivate([AzureAuthGuard, LabGuard, BUGuard]), data: { claim: [environment.AZURE_USER_GROUP_CLAIM, environment.AZURE_USER_DK_GROUP_CLAIM]} },
      { path: 'Storage',component:MaStorageComponent,canActivate: mapToCanActivate([AzureAuthGuard, LabGuard, BUGuard]), data: { claim: [environment.AZURE_USER_GROUP_CLAIM, environment.AZURE_USER_DK_GROUP_CLAIM]} },
      { path: 'machines', component: MaMachineComponent, canActivate: mapToCanActivate([AzureAuthGuard, LabGuard, BUGuard]), data: { claim: [environment.AZURE_USER_GROUP_CLAIM, environment.AZURE_USER_DK_GROUP_CLAIM] } },
      { path: 'Status-Screen',component:StatusScreenComponent,canActivate: mapToCanActivate([AzureAuthGuard, LabGuard, BUGuard]), data: { claim: [environment.AZURE_USER_GROUP_CLAIM, environment.AZURE_USER_DK_GROUP_CLAIM]} },
      { path: 'NG-reception',component:NGReceptionComponent,canActivate: mapToCanActivate([AzureAuthGuard, LabGuard, BUGuard]), data: { claim: [environment.AZURE_USER_GROUP_CLAIM, environment.AZURE_USER_DK_GROUP_CLAIM]} },
      { path: 'Run',component:RunComponent,canActivate: mapToCanActivate([AzureAuthGuard, LabGuard, BUGuard]), data: { claim: [environment.AZURE_USER_GROUP_CLAIM, environment.AZURE_USER_DK_GROUP_CLAIM]} },
      { path: 'process', component: NgsProcessComponent, canActivate: mapToCanActivate([AzureAuthGuard, LabGuard, BUGuard]), data: { claim: [environment.AZURE_USER_GROUP_CLAIM, environment.AZURE_USER_DK_GROUP_CLAIM] } },
      { path: 'ICBF-Workflow', component: ICBFWorkflowComponent, canActivate: mapToCanActivate([AzureAuthGuard, LabGuard, BUGuard]), data: { claim: [environment.AZURE_USER_GROUP_CLAIM, environment.AZURE_USER_DK_GROUP_CLAIM] } },

      //New UI Routes
      {
        path: 'configuration',
        canActivate: mapToCanActivate([AzureAuthGuard, LabGuard, BUGuard]), canDeactivate:[unsavedChangesGuard], data: { claim: ALL_USER_ACCESS },
        loadChildren: () => import(  './configuration/configuration.module').then(m => m.ConfigurationModule)
      },
      {
        path: 'samples-new',
        canActivate: mapToCanActivate([AzureAuthGuard, LabGuard, BUGuard]), data: { claim: ALL_USER_ACCESS },
        loadChildren: () => import( './samples/samples.module').then(m =>m.SamplesModule)
      },
      { path: 'history', component:HistoryComponent, canActivate: mapToCanActivate([AzureAuthGuard,LabGuard,BUGuard]), data: { claim: ALL_USER_ACCESS } },
      {
        path: 'delivery',
        canActivate: mapToCanActivate([AzureAuthGuard, LabGuard, BUGuard]), data: { claim: ALL_USER_ACCESS },
        loadChildren: () => import( './delivery/delivery.module').then(m =>m.DeliveryModule)
      },
      {
        path: 'reception-new',
        canActivate: mapToCanActivate([AzureAuthGuard, LabGuard, BUGuard]), data: { claim: ALL_USER_ACCESS },
        loadChildren: () => import( './reception-new/reception.module').then(m =>m.ReceptionModule)
      },
      {
        path: 'preparation-new',
        canActivate: mapToCanActivate([AzureAuthGuard, LabGuard, BUGuard]), data: { claim: ALL_USER_ACCESS },
        loadChildren: () => import( './preparation/preparation.module').then(m =>m.PreparationModule)
      },
      { path: 'storage-new', component: StorageNewComponent, canActivate: mapToCanActivate([AzureAuthGuard, LabGuard, BUGuard]), data: { claim: ALL_USER_ACCESS} },
      { path: 'machines-new', component: MachinesNewComponent, canActivate: mapToCanActivate([AzureAuthGuard, LabGuard, BUGuard]), data: { claim: ALL_USER_ACCESS} }
    ]),
  ],
    providers: [
    LabGuard,
    LabEmitterService,
    CarrierService,
    Globals,
    MessagesService,
    MessageService,
    MasterDataService,
    EventEmitterService,
    { provide: HTTP_INTERCEPTORS, useClass: AccessTokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    AuthGuard,
    AzureAuthGuard,
    MdmGuard,
    AccessTokenGuard,
    BUGuard,
    AuthenticationService,
    ConfirmationService,
    MdmProcessStepService,
    DatePipe,
    AppService,
    AzureAppInsightsService,
    ApgDeliveryService,
    RefreshAuthenticationService,
    APGMachineStatusService,
    APGAdministrationService,
    MaReceptionService,
    MaPlatePreparationService,
    MaResultEvaluationService,
    MaMachineService,
    RunPlanningService,
    RunSchedulingService,
    RunProcessingService,
    APGTestingService,
    NumberFormatPipe,

    //New UI Services
    MessagesNewService,
    ConfigurationService,
    PlateProcessingService,
    NavOpenEmitterService,
    MachineNewService
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
